// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: what a file is for previewing, from its name, type and size. The one list Konvo shows files
// by: chat, mail and WhatsApp attachments, Ask, Media and files, and Agents (files and attachments)
// all ask here, so a kind added once shows everywhere. FileContentPreview draws each kind and
// FilePreviewWindow is the one window around it.

import {DOCUMENT_PREVIEW_MAX_BYTES, documentKind} from '@app/features/document_preview/DocumentPreviewUtils';

export type PreviewKind =
	| 'image'
	| 'pdf'
	| 'text'
	| 'csv'
	| 'html'
	| 'markdown'
	| 'audio'
	| 'video'
	| 'docx'
	| 'spreadsheet'
	| 'presentation'
	| 'none';

export interface PreviewAttachment {
	name: string;
	content_type: string;
	size: number;
}

const PRESENTATION_TYPES = new Set([
	'application/vnd.openxmlformats-officedocument.presentationml.presentation',
	'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
	'application/vnd.openxmlformats-officedocument.presentationml.template',
	'application/vnd.ms-powerpoint.presentation.macroenabled.12',
]);

const CSV_TYPES = new Set(['text/csv', 'application/csv', 'text/tab-separated-values']);
const TEXT_TYPES = new Set([
	'application/json',
	'application/ld+json',
	'application/xml',
	'application/javascript',
	'application/x-sh',
	'application/x-shellscript',
	'application/x-bash',
	'application/sql',
	'application/x-yaml',
	'package/manifest+json',
]);
// By extension, or a whole name such as Dockerfile.
const TEXT_EXTENSIONS =
	/(?:^|\.)(txt|log|json|jsonc|jsonl|xml|yaml|yml|toml|ini|cfg|conf|env|sql|graphql|js|jsx|mjs|cjs|ts|tsx|mts|cts|py|java|kt|swift|c|cpp|h|hpp|cs|go|rs|rust|rb|php|lua|dart|sh|bash|zsh|ps1|css|scss|sass|less|vue|svelte|dockerfile|diff|patch)$/i;

/** Text, CSV and code files preview up to this size; larger ones say so and download. */
export const PREVIEW_TEXT_MAX_BYTES = 2 * 1024 * 1024;

/** Whether a file is comma- or tab-separated values, from its name or type. */
function isSeparatedValues(name: string, type: string): boolean {
	return CSV_TYPES.has(type) || /\.(csv|tsv)$/i.test(name);
}

/** The column separator of a CSV or TSV file. */
export function csvDelimiterFor(name: string, contentType = ''): ',' | '\t' {
	const type = contentType.toLowerCase().split(';')[0].trim();
	return /\.tsv$/i.test(name) || type === 'text/tab-separated-values' ? '\t' : ',';
}

/**
 * What a file is, whatever its size: the one list Konvo decides how a file shows by. Use
 * previewKindFor to know whether it previews now; this one tells a file too large to preview
 * from a kind Konvo can't show at all.
 */
export function fileKindFor(attachment: Pick<PreviewAttachment, 'name' | 'content_type'>): PreviewKind {
	const {name} = attachment;
	const type = attachment.content_type.toLowerCase().split(';')[0].trim();
	// Before spreadsheets: Windows sends .csv files as application/vnd.ms-excel.
	if (isSeparatedValues(name, type)) return 'csv';
	if (type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || /\.docx$/i.test(name))
		return 'docx';
	if (
		['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'].includes(type) ||
		/\.xlsx?$/i.test(name)
	)
		return 'spreadsheet';
	// Only the zipped formats (2007 onwards); old binary .ppt files still download.
	if (PRESENTATION_TYPES.has(type) || /\.(pptx|ppsx|pptm|potx)$/i.test(name)) return 'presentation';
	if (type.startsWith('image/') || /\.(png|jpe?g|gif|webp|avif|bmp|svg|ico)$/i.test(name)) return 'image';
	if (type === 'application/pdf' || /\.pdf$/i.test(name)) return 'pdf';
	if (type.startsWith('audio/') || /\.(mp3|wav|ogg|oga|opus|m4a|aac|flac)$/i.test(name)) return 'audio';
	if (type.startsWith('video/') || /\.(mp4|m4v|webm|mov)$/i.test(name)) return 'video';
	// KONVO: HTML pages and Markdown documents show as the page or document (document_preview).
	const doc = documentKind(name, type === 'application/octet-stream' ? '' : type);
	if (doc) return doc;
	if (type.startsWith('text/') || TEXT_TYPES.has(type) || TEXT_EXTENSIONS.test(name)) return 'text';
	return 'none';
}

/** The most a file of this kind previews at, or null for no limit. */
export function previewMaxBytes(kind: PreviewKind): number | null {
	switch (kind) {
		case 'text':
		case 'csv':
			return PREVIEW_TEXT_MAX_BYTES;
		case 'html':
		case 'markdown':
			return DOCUMENT_PREVIEW_MAX_BYTES;
		default:
			return null;
	}
}

/** How a file previews now: its kind, or 'none' for a kind Konvo can't show or a file over its kind's limit. */
export function previewKindFor(attachment: PreviewAttachment): PreviewKind {
	const kind = fileKindFor(attachment);
	const max = previewMaxBytes(kind);
	return max !== null && attachment.size > max ? 'none' : kind;
}

/** A file Konvo would show but for its size: it says so, rather than "no preview". */
export function isTooLargeToPreview(attachment: PreviewAttachment): boolean {
	return previewKindFor(attachment) === 'none' && fileKindFor(attachment) !== 'none';
}

/**
 * Whether PDFs need the in-app (pdf.js) viewer rather than the browser's own in a frame.
 * Android has no PDF viewer in Chrome or the app's WebView, and iOS and iPadOS draw only the
 * first page of a framed PDF, so touch-only devices always get the in-app viewer.
 */
export function shouldRenderPdfInApp(): boolean {
	if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;
	if (navigator.pdfViewerEnabled === false) return true;
	if (typeof window.matchMedia !== 'function') return false;
	return window.matchMedia('(pointer: coarse)').matches && !window.matchMedia('(any-pointer: fine)').matches;
}
